import { useEffect } from "react"

// Immersion façon pages produit Apple, appliquée à toute une page en un hook :
// ① révélation en cascade des blocs de section — JS-only (le HTML servi reste
//    intégralement visible : zéro impact SEO/no-JS), et on ne cache JAMAIS un
//    élément déjà dans le viewport au chargement (zéro flash) ;
// ② compteurs [data-count] : le premier nombre du bloc s'incrémente à l'entrée
//    dans l'écran (ease-out quartique, formatage fr conservé) ;
// ③ héros qui se dissout : le texte monte plus lentement que le média et se
//    fond pendant le scroll (rAF passif).
// prefers-reduced-motion neutralise les trois.
const EASE = "cubic-bezier(0.22,1,0.36,1)"

const CONTAINER_SELECTOR = [
  ".mg-section", ".mg-section-soft", ".mg-section-dark",
  ".hp-section", ".hp-section-soft", ".hp-section-dark",
  ".qp-section", ".qp-section-soft", ".qp-section-dark",
]
  .map(s => `${s} .mg-container, ${s} .mg-container-wide, ${s} .hp-container, ${s} .hp-container-wide, ${s} .qp-container`)
  .join(", ") + ", [data-immersion-scope]"

export default function useImmersion() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const cleanups = []

    // ── ① Révélation en cascade ──────────────────────────────────────────────
    if (!reduced && "IntersectionObserver" in window) {
      const units = []
      document.querySelectorAll(CONTAINER_SELECTOR).forEach(container => {
        ;[...container.children].forEach(child => {
          // une grille de cartes se révèle carte par carte, le reste par bloc
          const isGrid =
            getComputedStyle(child).display === "grid" &&
            child.children.length > 2 &&
            child.children.length <= 14
          const targets = isGrid ? [...child.children] : [child]
          targets.forEach(el => {
            const r = el.getBoundingClientRect()
            if (r.top < window.innerHeight * 0.9) return // déjà visible : on ne cache pas
            if (r.height > window.innerHeight * 1.4) return // bloc géant : on ne touche pas
            el.style.opacity = "0"
            el.style.transform = "translateY(26px)"
            units.push(el)
          })
        })
      })
      let batch = []
      let flushTimer = 0
      const flush = () => {
        batch.forEach((el, i) => {
          const delay = Math.min(i * 70, 420)
          el.style.transition = `opacity 750ms ${EASE} ${delay}ms, transform 750ms ${EASE} ${delay}ms`
          el.style.opacity = "1"
          el.style.transform = "translateY(0)"
        })
        batch = []
      }
      const io = new IntersectionObserver(
        entries => {
          let touched = false
          entries.forEach(e => {
            if (!e.isIntersecting) return
            io.unobserve(e.target)
            batch.push(e.target)
            touched = true
          })
          if (touched) {
            clearTimeout(flushTimer)
            flushTimer = setTimeout(flush, 40) // groupe les entrées d'un même "écran"
          }
        },
        { rootMargin: "0px 0px -8% 0px" }
      )
      units.forEach(el => io.observe(el))
      cleanups.push(() => { io.disconnect(); clearTimeout(flushTimer) })
    }

    // ── ② Compteurs [data-count] ─────────────────────────────────────────────
    const counters = document.querySelectorAll("[data-count]")
    if (counters.length) {
      const animate = el => {
        const text = el.textContent
        const m = text.match(/\d[\d\s .,]*\d|\d/)
        if (!m) return
        const raw = m[0]
        // décimale (« 2,3 M$ », « $2.3M », « 1,74 M$ ») : on anime en flottant et on
        // conserve le séparateur et le nombre de décimales — sinon « 2,3 » devenait « 23 »
        const dec = raw.match(/^(\d[\d\s]*)([.,])(\d{1,2})$/)
        const target = dec
          ? parseFloat(dec[1].replace(/[^\d]/g, "") + "." + dec[3])
          : parseInt(raw.replace(/[^\d]/g, ""), 10)
        if (!target) return
        if (reduced) return // valeur finale déjà dans le HTML
        const prefix = text.slice(0, m.index)
        const suffix = text.slice(m.index + raw.length)
        const grouped = !dec && /[\s .,]/.test(raw)
        const fmt = n => dec
          ? n.toFixed(dec[3].length).replace(".", dec[2])
          : (grouped ? n.toLocaleString("fr-FR") : String(n))
        const start = performance.now()
        const dur = 1100
        // filet de sécurité : si la chaîne rAF cale (onglet masqué, throttling),
        // la valeur finale est écrite quoi qu'il arrive — jamais un chiffre faux à l'écran
        const failsafe = setTimeout(() => { el.textContent = prefix + fmt(target) + suffix }, dur + 150)
        cleanups.push(() => clearTimeout(failsafe))
        const tick = now => {
          const p = Math.min(1, (now - start) / dur)
          const eased = 1 - Math.pow(1 - p, 4)
          el.textContent = prefix + fmt(dec ? target * eased : Math.round(target * eased)) + suffix
          if (p < 1) requestAnimationFrame(tick)
          else clearTimeout(failsafe)
        }
        requestAnimationFrame(tick)
      }
      const cio = new IntersectionObserver(
        entries => entries.forEach(e => { if (e.isIntersecting) { cio.unobserve(e.target); animate(e.target) } }),
        { threshold: 0.6 }
      )
      counters.forEach(el => cio.observe(el))
      cleanups.push(() => cio.disconnect())
    }

    // ── ③ Héros qui se dissout ───────────────────────────────────────────────
    if (!reduced) {
      const page = document.querySelector(".mg-page, .hp-page, [data-immersion-page]")
      const hero = page ? page.querySelector("section") : document.querySelector("[data-holiday-hero]")
      const content =
        hero && [...hero.children].find(el => ["2", "10"].includes(getComputedStyle(el).zIndex))
      if (hero && content && hero.offsetHeight > window.innerHeight * 0.6) {
        let raf = 0
        const update = () => {
          raf = 0
          const y = window.scrollY
          const h = hero.offsetHeight
          if (y > h) return
          const p = Math.min(1, y / (h * 0.7))
          content.style.transform = `translate3d(0, ${(y * 0.28).toFixed(1)}px, 0)`
          content.style.opacity = Math.max(0, 1 - p * 1.15).toFixed(3)
        }
        const onScroll = () => { if (!raf) raf = requestAnimationFrame(update) }
        window.addEventListener("scroll", onScroll, { passive: true })
        window.addEventListener("resize", onScroll, { passive: true })
        update()
        cleanups.push(() => {
          window.removeEventListener("scroll", onScroll)
          window.removeEventListener("resize", onScroll)
          if (raf) cancelAnimationFrame(raf)
        })
      }
    }

    return () => cleanups.forEach(fn => fn())
  }, [])
}
